import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import productsService from '../services/productsService';
import './ProductsList.css';

const EMPTY_FORM = { name: '', display_name: '' };

export default function ProductsList() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState(null);
    const [addModal, setAddModal] = useState(null);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await productsService.list();
            setProducts(res.data?.products || []);
        } catch (err) {
            flash(err.response?.data?.error?.message || 'Failed to load products', 'error');
        } finally {
            setLoading(false);
        }
    };

    const flash = (text, type = 'success') => {
        setMsg({ type, text });
        setTimeout(() => setMsg(null), 4000);
    };

    const save = async () => {
        if (!addModal.name.trim() || !addModal.display_name.trim()) return;
        setSaving(true);
        try {
            const res = await productsService.create({ name: addModal.name.trim(), display_name: addModal.display_name.trim() });
            setAddModal(null);
            navigate(`/admin/products/${res.data.product.id}`);
        } catch (err) {
            flash(err.response?.data?.error?.message || 'Failed to create product', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="products-container">
            <div className="products-header">
                <div>
                    <h1>Products</h1>
                    <p className="products-subtitle">Products sold on the platform.</p>
                </div>
                <button className="btn-primary" onClick={() => setAddModal({ ...EMPTY_FORM })}>+ Add Product</button>
            </div>

            {msg && <div className={`msg-banner msg-${msg.type}`}>{msg.text}</div>}

            {loading ? (
                <div className="products-loading">Loading...</div>
            ) : products.length === 0 ? (
                <div className="products-empty">No products yet. Add one to get started.</div>
            ) : (
                <table className="products-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Display Name</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map(p => (
                            <tr key={p.id}>
                                <td className="product-name">{p.name}</td>
                                <td>{p.display_name}</td>
                                <td className="product-actions">
                                    <button className="btn-secondary btn-sm" onClick={() => navigate(`/admin/products/${p.id}`)}>
                                        Manage
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {addModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Add Product</h3>
                        <div className="form-group">
                            <label>Name *</label>
                            <input
                                type="text"
                                value={addModal.name}
                                onChange={e => setAddModal(m => ({ ...m, name: e.target.value }))}
                                placeholder="e.g. vault"
                                autoFocus
                            />
                            <p className="field-hint">Internal identifier — lowercase, no spaces (e.g. <code>vault</code>, <code>sync</code>).</p>
                        </div>
                        <div className="form-group">
                            <label>Display Name *</label>
                            <input
                                type="text"
                                value={addModal.display_name}
                                onChange={e => setAddModal(m => ({ ...m, display_name: e.target.value }))}
                                placeholder="e.g. Vault"
                            />
                        </div>
                        <div className="modal-actions">
                            <button className="btn-secondary" onClick={() => setAddModal(null)}>Cancel</button>
                            <button
                                className="btn-primary"
                                onClick={save}
                                disabled={saving || !addModal.name.trim() || !addModal.display_name.trim()}
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
